import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { parkingApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface ParkingLot {
  id: string
  name: string
  number: string
  slots_start: number
  slots_end: number
  timeout: number
  music_on_hold: string
  announce_slot: boolean
  comeback_dial: string
  is_active: boolean
}

const emptyForm = {
  name: '',
  number: '',
  slots_start: 701,
  slots_end: 720,
  timeout: 45,
  music_on_hold: 'default',
  announce_slot: true,
  comeback_dial: '',
  is_active: true
}

export default function Parking() {
  const confirm = useConfirm()
  const [parkingLots, setParkingLots] = useState<ParkingLot[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingLot, setEditingLot] = useState<ParkingLot | null>(null)
  const [formData, setFormData] = useState(emptyForm)

  useEffect(() => {
    loadParkingLots()
  }, [])

  const loadParkingLots = async () => {
    try {
      const response = await parkingApi.list()
      setParkingLots(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingLot(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (lot: ParkingLot) => {
    setEditingLot(lot)
    setFormData({
      name: lot.name,
      number: lot.number,
      slots_start: lot.slots_start || 701,
      slots_end: lot.slots_end || 720,
      timeout: lot.timeout || 45,
      music_on_hold: lot.music_on_hold || 'default',
      announce_slot: lot.announce_slot ?? true,
      comeback_dial: lot.comeback_dial || '',
      is_active: lot.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingLot) {
        await parkingApi.update(editingLot.id, formData)
        toast.success('Parkzone aktualisiert')
      } else {
        await parkingApi.create(formData)
        toast.success('Parkzone erstellt')
      }
      setShowModal(false)
      loadParkingLots()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (lot: ParkingLot) => {
    const confirmed = await confirm({
      title: 'Parkzone löschen',
      message: `Parkzone "${lot.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await parkingApi.delete(lot.id)
      toast.success('Parkzone gelöscht')
      loadParkingLots()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Call Parking</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Parkzone
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Name</th>
              <th>Slots</th>
              <th>Timeout</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : parkingLots.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">Keine Parkzonen</td></tr>
            ) : (
              parkingLots.map(lot => (
                <tr key={lot.id}>
                  <td><strong>{lot.number}</strong></td>
                  <td>{lot.name}</td>
                  <td><Badge bg="info">{lot.slots_start} - {lot.slots_end}</Badge></td>
                  <td>{lot.timeout} Sek.</td>
                  <td><Badge bg={lot.is_active ? 'success' : 'secondary'}>{lot.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(lot)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(lot)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingLot ? 'Parkzone bearbeiten' : 'Neue Parkzone'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nummer *</Form.Label>
                  <Form.Control
                    value={formData.number}
                    onChange={(e) => setFormData({...formData, number: e.target.value})}
                    required
                    placeholder="z.B. 700"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Hauptparkzone"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Card className="mb-3">
              <Card.Header>Parkplätze</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Start-Slot</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.slots_start}
                        onChange={(e) => setFormData({...formData, slots_start: parseInt(e.target.value) || 701})}
                        min={1}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>End-Slot</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.slots_end}
                        onChange={(e) => setFormData({...formData, slots_end: parseInt(e.target.value) || 720})}
                        min={formData.slots_start + 1}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Timeout (Sek.)</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => setFormData({...formData, timeout: parseInt(e.target.value) || 45})}
                    min={10}
                    max={600}
                  />
                  <Form.Text className="text-muted">Zeit bis Rückruf</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Rückrufnummer</Form.Label>
                  <Form.Control
                    value={formData.comeback_dial}
                    onChange={(e) => setFormData({...formData, comeback_dial: e.target.value})}
                    placeholder="Leer = Parker"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="announce_slot"
                    label="Slot-Nummer ansagen"
                    checked={formData.announce_slot}
                    onChange={(e) => setFormData({...formData, announce_slot: e.target.checked})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_active"
                    label="Aktiv"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingLot ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
